import { useEffect } from "react";
import type { Channel } from "@hybrid-iptv/types";
import { providerBody } from "../epg/api.js";
import { useSettings } from "../settings/settingsStore.js";
import { dvr, dvrAvailable, type DvrChannelEntry } from "./dvr.js";
import { baseFeedChannelId, feedChannelShift, isExtraFeedChannel } from "../api/nativeGuide.js";

/**
 * Hands the Android app's DVR what its series check needs: the guide
 * feed, the Xtream login (for the provider's stream limit), and every channel
 * the guide covers with its stream URL — mapped the same way the guide screen
 * maps them, so a series rule's channel is the channel the viewer saw.
 *
 * Sent once per change of source, channel list or guide mapping, not on every
 * guide refresh.
 */

/**
 * Feed channel id → the catalog channels the guide matched to it. `guideShift`:
 * the own guide's shift (Phase 3 step 4); a channel shifted on its own carries
 * its shift in its id, and the DVR is told the guide channel itself and the shift.
 */
export function buildDvrChannels(
  nameMapping: Record<string, string[]> | undefined,
  channels: readonly Channel[],
  hydrate: (url: string) => string,
  feedNames?: ReadonlyMap<string, string>,
  guideShift = 0,
): DvrChannelEntry[] {
  if (!nameMapping) return [];
  const byName = new Map<string, Channel>();
  for (const channel of channels) {
    const key = channel.name.toLowerCase().trim();
    if (key && !byName.has(key)) byName.set(key, channel);
  }
  const entries: DvrChannelEntry[] = [];
  const seen = new Set<string>();
  for (const [answerId, names] of Object.entries(nameMapping)) {
    // Series rules follow the playlist's own guide (Phase 3 step 3): an extra
    // source's channels are the page's, not the DVR's.
    if (isExtraFeedChannel(answerId)) continue;
    const feedChannelId = baseFeedChannelId(answerId);
    const shiftMinutes = feedChannelShift(answerId, guideShift);
    for (const name of names) {
      const channel = byName.get(name.toLowerCase().trim());
      if (!channel || seen.has(`${feedChannelId}|${channel.id}`)) continue;
      seen.add(`${feedChannelId}|${channel.id}`);
      const feedName = feedNames?.get(feedChannelId);
      entries.push({
        feedChannelId,
        ...(feedName ? { feedName } : {}),
        channelId: channel.id,
        name: channel.name,
        url: hydrate(channel.streamUrl),
        ...(shiftMinutes ? { shiftMinutes } : {}),
      });
    }
  }
  return entries;
}

/**
 * What the DVR was last told, kept across launches. Held only in memory, it
 * reset with every page load, so each cold start rebuilt all ~4,800 channels
 * (stream URLs and all), sent them over the bridge and had native rewrite a
 * 1 MB file — while Home was trying to come up (projector, 2026-09-26). The
 * guide's fetch time is part of it, so a fresh guide still re-tells the DVR.
 */
const DVR_CHANNELS_KEY = "slq.dvrChannels";

function readLastSignature(): string {
  try {
    return localStorage.getItem(DVR_CHANNELS_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeLastSignature(signature: string): void {
  try {
    if (signature) localStorage.setItem(DVR_CHANNELS_KEY, signature);
    else localStorage.removeItem(DVR_CHANNELS_KEY);
  } catch {
    // Without storage the DVR is re-told on every launch, which is harmless.
  }
}

/** Held back so the sync never competes with a cold start for the page's thread. */
const DVR_SYNC_DELAY_MS = 20_000;

/** The source the DVR was last given, as a hash — no login is kept here. */
const DVR_SOURCE_KEY = "slq.dvrSource";

function readDvrSourceKey(): string | null {
  try {
    return localStorage.getItem(DVR_SOURCE_KEY);
  } catch {
    return null;
  }
}

function writeDvrSourceKey(key: string): void {
  try {
    localStorage.setItem(DVR_SOURCE_KEY, key);
  } catch {
    // Without storage the DVR is re-told on every launch, which is harmless.
  }
}

/** A short, non-reversible identity for a source: which account, not its password. */
export function dvrSourceKey(source: { epgUrl?: string; xtreamBaseUrl?: string; xtreamUsername?: string; xtreamPassword?: string } | null): string {
  const text = source ? `${source.xtreamBaseUrl ?? ""}|${source.xtreamUsername ?? ""}|${source.epgUrl ?? ""}` : "none";
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) hash = ((hash * 33) ^ text.charCodeAt(i)) >>> 0;
  return `${text === "none" ? "none" : "src"}-${hash.toString(36)}`;
}

/**
 * The moment the viewer switches source, tell the DVR — before the new guide
 * has loaded. The channel-list sync below waits for the guide, and until
 * 2026-09-17 a source the DVR could not read (a plain M3U) never reached it at
 * all: the DVR kept checking the OLD account's guide and multiview used the old
 * account's stream limit. Only a CHANGE of account is sent, so a normal launch
 * leaves the DVR's channel list alone while the guide loads.
 */
export function useDvrSourceIdentity(): void {
  // The provider object, not its id: editing a source's login keeps the id.
  const activeProvider = useSettings((s) => s.activeProvider);
  useEffect(() => {
    if (!dvrAvailable()) return;
    void (async () => {
      const { deviceDvrSource } = await import("../api/deviceApi.js");
      const source = deviceDvrSource(providerBody());
      const key = dvrSourceKey(source);
      const previous = readDvrSourceKey();
      if (previous === key) return;
      if (previous === null && source) {
        // First launch of this version with a readable source: the guide sync
        // hands the DVR this same account shortly; do not empty its channels.
        writeDvrSourceKey(key);
        return;
      }
      try {
        // No channels yet: the old account's list is wrong for the new one; the
        // guide sync fills the new list in once it has loaded.
        await dvr.syncSource(source ? { ...source, channels: [] } : { channels: [] });
        writeDvrSourceKey(key);
        writeLastSignature("");
      } catch {
        // Try again on the next change.
      }
    })();
  }, [activeProvider]);
}

export function useDvrSourceSync(
  sourceId: string,
  epgData: { nameMapping?: Record<string, string[]>; channels?: unknown; fetchedAt?: string; guideShift?: number } | undefined,
  channels: readonly Channel[],
): void {
  const mapping = epgData?.nameMapping;
  const mappedCount = mapping ? Object.keys(mapping).length : 0;
  useEffect(() => {
    if (!dvrAvailable() || !mapping || mappedCount === 0 || channels.length === 0) return;
    // A changed shift re-tells the DVR too: the guide's own, and the channels shifted on their own.
    let shifted = 0;
    for (const id of Object.keys(mapping)) {
      if (baseFeedChannelId(id) === id) continue;
      for (let i = 0; i < id.length; i += 1) shifted = ((shifted * 33) ^ id.charCodeAt(i)) >>> 0;
    }
    const shiftKey = `${epgData?.guideShift ?? 0}:${shifted.toString(36)}`;
    const signature = `${sourceId}|${channels.length}|${mappedCount}|${epgData?.fetchedAt ?? ""}|${shiftKey}`;
    if (signature === readLastSignature()) return;
    const timer = setTimeout(() => void (async () => {
      if (signature === readLastSignature()) return;
      const [{ deviceDvrSource }, { hydrateRedactedUrl, providerCredentials }] = await Promise.all([
        import("../api/deviceApi.js"),
        import("@hybrid-iptv/iptv-parser"),
      ]);
      const body = providerBody();
      const source = deviceDvrSource(body);
      if (!source) return;
      const { username, password } = providerCredentials(body);
      const feedNames = new Map<string, string>();
      for (const entry of Array.isArray(epgData?.channels) ? (epgData.channels as Array<{ id?: string; displayName?: string }>) : []) {
        if (entry.id && entry.displayName) feedNames.set(entry.id, entry.displayName);
      }
      const entries = buildDvrChannels(mapping, channels, (url) => hydrateRedactedUrl(url, username, password), feedNames, epgData?.guideShift ?? 0);
      try {
        await dvr.syncSource({ ...source, channels: entries });
        writeDvrSourceKey(dvrSourceKey(source));
        writeLastSignature(signature);
      } catch {
        // Try again on the next change.
      }
    })(), DVR_SYNC_DELAY_MS);
    return () => clearTimeout(timer);
  }, [sourceId, mapping, mappedCount, channels, epgData]);
}
